import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";

import { Utils } from "../../../../../shared/utils/Utils";
import {
  TipoPrograma,
  TipoProgramaService,
} from "../../../../../shared/services/tipo-programa.service";

@Component({
  selector: "app-tipo-programa",
  templateUrl: "./tipo-programa.component.html",
  styleUrls: ["./tipo-programa.component.scss"],
})
export class TipoProgramaComponent implements OnInit {
  // Expuesto para poder usarlo en el template (Number(tipo.estado))
  readonly Number = Number;

  tipoProgramaForm!: FormGroup;

  // Listado
  tipos: TipoPrograma[] = [];
  tiposFiltrados: TipoPrograma[] = [];
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;
  cargandoListado = false;

  // Modal crear/editar (un solo modal para ambos modos)
  modoEdicion = false;
  idEditando: number | null = null;
  guardando = false;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private tipoProgramaService: TipoProgramaService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarTipos();
  }

  private inicializarFormulario(): void {
    this.tipoProgramaForm = this.fb.group({
      descripcion: ["", [Validators.required, Validators.maxLength(250)]],
    });
  }

  private normalizarLista(response: any): TipoPrograma[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  cargarTipos(): void {
    this.cargandoListado = true;
    this.tipoProgramaService.getTipos().subscribe({
      next: (response: any) => {
        this.tipos = this.normalizarLista(response);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.tipos = [];
        this.tiposFiltrados = [];
        this.cargandoListado = false;
        Swal.fire("Error", "No se pudieron cargar los tipos de programa.", "error");
      },
    });
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "").toLowerCase().trim();

    let filtrados = this.tipos.filter((tipo) => {
      const coincideTexto =
        !termino || String(tipo.descripcion || "").toLowerCase().includes(termino);

      const estadoTipo = Number(tipo.estado);
      const coincideEstado =
        this.filtroEstado === null || estadoTipo === this.filtroEstado;

      return coincideTexto && coincideEstado;
    });

    filtrados = filtrados.sort((a, b) => {
      const estadoA = Number(a.estado) === 1 ? 0 : 1;
      const estadoB = Number(b.estado) === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return (a.id || 0) - (b.id || 0);
    });

    this.tiposFiltrados = filtrados;
    this.page = 1;
  }

  // 🆕 Abre el modal en modo creación
  abrirModalCrear(modalRef: any): void {
    this.modoEdicion = false;
    this.idEditando = null;
    this.tipoProgramaForm.reset();
    this.modalService.open(modalRef, { centered: true, size: "lg", backdrop: "static" });
  }

  // 🆕 Abre el modal en modo edición, precargando el registro
  abrirModalEditar(modalRef: any, tipo: TipoPrograma): void {
    if (Number(tipo?.estado) !== 1) {
      Swal.fire("Info", "Solo se puede editar cuando el registro está activo.", "info");
      return;
    }

    this.modoEdicion = true;
    this.idEditando = Number(tipo.id);
    this.tipoProgramaForm.patchValue({
      descripcion: tipo.descripcion,
    });

    this.modalService.open(modalRef, { centered: true, size: "lg", backdrop: "static" });
  }

  guardar(modal: any): void {
    if (this.tipoProgramaForm.invalid) {
      this.tipoProgramaForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Completa todos los campos requeridos para guardar.",
        "warning",
      );
      return;
    }

    const descripcion = String(this.tipoProgramaForm.get("descripcion")?.value || "")
      .trim()
      .toUpperCase();

    const payload: TipoPrograma = { descripcion };

    this.guardando = true;

    const peticion$ = this.modoEdicion && this.idEditando
      ? this.tipoProgramaService.updateTipo(this.idEditando, payload)
      : this.tipoProgramaService.createTipo({ ...payload, estado: 1 });

    peticion$.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire(
          "Éxito",
          `Tipo de programa ${this.modoEdicion ? "actualizado" : "creado"} correctamente.`,
          "success",
        );
        modal.close();
        this.tipoProgramaForm.reset();
        this.cargarTipos();
      },
      error: (error: any) => {
        this.guardando = false;
        const mensaje =
          error?.error?.error ||
          `No se pudo ${this.modoEdicion ? "actualizar" : "crear"} el tipo de programa.`;
        Swal.fire("Error", mensaje, "error");
      },
    });
  }

  cambiarEstado(tipo: TipoPrograma, estado: number): void {
    const id = Number(tipo?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id del tipo de programa.", "error");
      return;
    }

    const accion = estado === 1 ? "activar" : "inactivar";
    Swal.fire({
      title: `¿Deseas ${accion} este tipo de programa?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const peticion$ =
        estado === 1
          ? this.tipoProgramaService.activar(id)
          : this.tipoProgramaService.inactivar(id);

      peticion$.subscribe({
        next: () => {
          Swal.fire("Éxito", `Tipo de programa ${accion}do correctamente.`, "success");
          this.cargarTipos();
        },
        error: (error: any) => {
          const mensaje =
            error?.error?.error || `No se pudo ${accion} el tipo de programa.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }
}